import { AnalyticsEventName } from '@dupe/common/constants'
import clsx from 'clsx'
import { FirebaseError } from 'firebase/app'
import {
  PhoneAuthProvider,
  RecaptchaVerifier,
  UserInfo,
  getAuth,
  linkWithCredential,
  linkWithPhoneNumber,
  signInWithPhoneNumber,
} from 'firebase/auth'
import { motion } from 'framer-motion'
import type { E164Number } from 'libphonenumber-js/core'
import Image from 'next/image'
import { useEffect, useState } from 'react'
import {
  formatPhoneNumberIntl,
  isValidPhoneNumber,
} from 'react-phone-number-input'
import PhoneInputWrapper from '~/src/components/PhoneInputWrapper'
import AuthProviderButtons from '~/src/components/auth/AuthProviderButtons'
import PhoneNumberVerification from '~/src/components/auth/PhoneNumberVerification'
import {
  CompleteStep,
  DupeValuePropOneStep,
  DupeValuePropTwoStep,
  InviteCodeStep,
  JobToBeDoneStep,
  NewsLetterStep,
  PaywallStep,
  QualityVsValueStep,
  ShoppingIntentStep,
} from '~/src/components/onboarding'
import DupeLogo from '~/src/components/svg/DupeLogo'
import LoaderIcon from '~/src/components/svg/LoaderIcon'
import { useAuthContext } from '~/src/context/AuthProvider'
import { useSearchContext } from '~/src/context/SearchProvider'
import useAnalytics from '~/src/hooks/useAnalytics'
import useAuthCallback from '~/src/hooks/useAuthCallback'
import useAuthUser from '~/src/hooks/useAuthUser'
import useValidateStripeSession from '~/src/hooks/useValidateStripeSession'
import { RECAPTCA_ID } from '~/src/lib/constants'
import app from '~/src/lib/firebase/firebaseApp'
import logger from '~/src/lib/logger'
import { cn } from '~/src/lib/utils'
import {
  OnboardingStep,
  useOnboardingStep,
} from '~/src/stores/useOnboardingStore'

const auth = getAuth(app)

interface Props {
  closeCallback?: () => void
}

const AuthContent = ({ closeCallback }: Props) => {
  const analytics = useAnalytics()

  const { authUser, loading: loadingAuth, authUserNotAnonymous } = useAuthUser()
  const {
    modalTitle,
    modalSubtitle,
    modalAsset,
    userData,
    loadingUserData,
    skipPhoneLink,
    disableClose,
    fullScreen,
    handleAuthSuccess,
    skipOnboarding,
  } = useAuthContext()

  const { pendingEmailAuth } = useAuthCallback()
  const { resetProcessing } = useSearchContext()

  /**
   * Auth Modal Flow:
   *
   * The modal handles three main states:
   * 1. 'signin' - Initial auth state where user can choose auth method
   * 2. 'phone' - Phone verification state (if required)
   * 3. 'onboarding' - Post-auth onboarding flow
   *
   * Auth Callback Execution:
   * - On successful auth + completed onboarding: handleAuthSuccess is called
   * - On close after auth but before onboarding: handleAuthSuccess is called
   * - On close before auth completion: resetProcessing is called
   *
   * This ensures that:
   * - Full completion triggers the callback with complete user data
   * - Partial completion (auth only) still maintains authenticated state
   * - Cancellation properly resets the UI state
   */

  const [recaptchaVerifier, setRecaptchaVerifier] = useState<
    RecaptchaVerifier | undefined
  >(undefined)

  const [type, setType] = useState<'signin' | 'onboarding' | 'phone' | null>(
    null
  )
  const [userPhoneLinked, setUserPhoneLinked] = useState<boolean>(false)

  const [validNumber, setValidNumber] = useState(false)
  const [phoneNumber, setPhoneNumber] = useState<E164Number>(
    userData?.phone as E164Number
  )
  const [verificationCode, setVerificationCode] = useState('')
  const [verificationId, setVerificationId] = useState('')
  const [firstName, setFirstName] = useState('')
  const [email, setEmail] = useState('')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [processing, setProcessing] = useState(false)

  const hasOnboardingData = !!(
    Object.entries(userData?.onboarding_data || {}).length ||
    Object.entries(userData?.metadata?.onboarding_data || {}).length
  )

  // Onboarding state
  const currentStep = useOnboardingStep()

  const stepsToShowProgress = [
    OnboardingStep.SHOPPING_INTENT,
    OnboardingStep.QUALITY_VS_VALUE,
    OnboardingStep.DUPE_VALUE_PROP_ONE,
    OnboardingStep.JOB_TO_BE_DONE,
    OnboardingStep.DUPE_VALUE_PROP_TWO,
  ]

  // Calculate progress percentage for onboarding
  const calculateProgress = () => {
    if (!stepsToShowProgress.length) return 0
    const currentIndex = stepsToShowProgress.indexOf(currentStep)
    if (currentIndex === -1) return 0
    return ((currentIndex + 1) / stepsToShowProgress.length) * 100
  }

  const userHasPhone = (providerData: UserInfo[]): boolean =>
    (providerData || []).some((pd) => pd.phoneNumber)

  const userAuthedWithPhone = (providerData: UserInfo[]): boolean =>
    providerData.length === 1 &&
    providerData[0]?.providerId === PhoneAuthProvider.PROVIDER_ID

  const handleVerificationCodeChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setVerificationCode(event.target.value)
  }

  const handleSendCode = async (type: 'sign-in' | 'auth-link' = 'sign-in') => {
    setErrorMessage(null)
    setProcessing(true)

    analytics.track(AnalyticsEventName.AuthModalSendCode, {
      type,
    })

    if (!validNumber) {
      const valid = isValidPhoneNumber(formatPhoneNumberIntl(phoneNumber))

      if (!valid) {
        setErrorMessage('Invalid phone number')
        setProcessing(false)
        return
      }
    }

    const phoneNumberFormatted = formatPhoneNumberIntl(phoneNumber)
    const valid = isValidPhoneNumber(phoneNumber)
    setValidNumber(valid)

    const appVerifier = !!recaptchaVerifier
      ? recaptchaVerifier
      : new RecaptchaVerifier(auth, RECAPTCA_ID, {
          size: 'invisible',
        })
    setRecaptchaVerifier(appVerifier)

    if (!appVerifier) {
      logger.debug('[AuthModal] - Error creating recaptcha verifier')

      analytics.track(AnalyticsEventName.AuthModalSendCodeError, {
        type,
        recaptchaVerifier,
      })

      setErrorMessage('Error sending SMS code')
      setProcessing(false)

      return
    }

    try {
      if (type === 'auth-link') {
        if (!auth.currentUser) {
          setErrorMessage('Error linking phone number')
          setProcessing(false)
          return
        }

        const result = await linkWithPhoneNumber(
          auth.currentUser,
          phoneNumberFormatted,
          appVerifier
        )

        setVerificationId(result.verificationId)
      } else if (type === 'sign-in') {
        const confirmationResult = await signInWithPhoneNumber(
          auth,
          phoneNumberFormatted,
          appVerifier
        )

        setVerificationId(confirmationResult.verificationId)
      }
      setProcessing(false)
    } catch (error) {
      // Handle error
      setErrorMessage('Error sending SMS code')
      setProcessing(false)
      logger.error('Error sending SMS:', { error })

      analytics.track(AnalyticsEventName.AuthModalSendCodeError, {
        type,
        error,
      })
    }
  }

  const handleLinkPhone = async () => {
    setErrorMessage(null)
    setProcessing(true)

    if (!verificationCode) {
      setErrorMessage('Please enter a code')
      setProcessing(false)
      return
    }

    if (!auth.currentUser) {
      setErrorMessage('Error linking phone number')
      setProcessing(false)
      return
    }

    try {
      const phoneCredential = PhoneAuthProvider.credential(
        verificationId,
        verificationCode
      )
      // complete the link with phone number
      await linkWithCredential(auth.currentUser, phoneCredential)

      // Set linked and move to onboarding if successful
      setUserPhoneLinked(true)
      setProcessing(false)
      setType('onboarding')
    } catch (error) {
      const fbError = error as FirebaseError
      // handle firebase error
      switch (fbError?.code) {
        case 'auth/credential-already-in-use':
        case 'auth/account-exists-with-different-credential':
          setErrorMessage('Phone number already linked to another account')
          break
        case 'auth/invalid-verification-code':
          setErrorMessage('Invalid code')
          break
        case 'auth/code-expired':
          setErrorMessage('Code expired')
          break
        default:
          // Handle error
          setErrorMessage('Error linking phone number')
      }
      setProcessing(false)
      logger.error('Error linking phone number:', { error })
    }
  }

  const renderOnboardingStep = (closeCallback?: () => void) => {
    let StepComponent = null

    // Comment out below to test onboarding with an existing user
    if (hasOnboardingData || skipOnboarding) {
      handleAuthSuccess(closeCallback)
      return null
    }

    switch (currentStep) {
      case OnboardingStep.WELCOME:
        StepComponent = NewsLetterStep
        break
      case OnboardingStep.INVITE_CODE:
        StepComponent = InviteCodeStep
        break
      case OnboardingStep.SHOPPING_INTENT:
        StepComponent = ShoppingIntentStep
        break
      case OnboardingStep.QUALITY_VS_VALUE:
        StepComponent = QualityVsValueStep
        break
      case OnboardingStep.DUPE_VALUE_PROP_ONE:
        StepComponent = DupeValuePropOneStep
        break
      case OnboardingStep.JOB_TO_BE_DONE:
        StepComponent = JobToBeDoneStep
        break
      case OnboardingStep.DUPE_VALUE_PROP_TWO:
        StepComponent = DupeValuePropTwoStep
        break
      case OnboardingStep.PAYWALL:
        StepComponent = PaywallStep
        break
      case OnboardingStep.COMPLETE:
        StepComponent = CompleteStep
        break
      default:
        return null
    }

    return (
      <>
        <div
          className={cn(
            'absolute left-0 right-0 top-20 transition-opacity md:top-24',
            {
              'opacity-0': !stepsToShowProgress.includes(currentStep),
              'opacity-100': stepsToShowProgress.includes(currentStep),
            }
          )}
        >
          <div className='mx-auto h-0.5 bg-black/10 md:left-[200px] md:right-[200px] md:max-w-xl dark:bg-white/20'>
            <div
              className='h-full bg-black transition-all duration-300 ease-in-out'
              style={{ width: `${calculateProgress()}%` }}
            />
          </div>
        </div>
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className='flex flex-1 flex-col justify-center px-6 pt-8 md:mx-auto md:max-w-lg md:justify-start md:pt-20'
        >
          <StepComponent closeModal={closeCallback} />
        </motion.div>
      </>
    )
  }

  const renderContent = (closeCallback?: () => void) => {
    switch (type) {
      case 'signin':
        return (
          <>
            <div className='mx-auto flex max-w-md flex-col pt-16 md:max-w-4xl md:flex-row md:items-center'>
              {modalAsset && (
                <div className='relative mx-auto mb-4 aspect-square overflow-hidden rounded-2xl md:mb-0'>
                  <Image
                    src={modalAsset}
                    alt='modal asset'
                    unoptimized
                    className='aspect-square max-w-[130px] rounded-2xl object-cover md:max-w-[390px]'
                    width={390}
                    height={390}
                    priority
                  />
                  <div className='absolute inset-0 bg-black/5' />
                </div>
              )}

              <div
                className={clsx(
                  'relative flex-1 space-y-4 px-4 md:flex md:flex-col md:items-center',
                  {
                    'md:pl-12': !!modalAsset,
                  }
                )}
              >
                <div className='px-4 text-center md:max-w-sm md:space-y-2 md:px-0'>
                  <div
                    className={clsx(
                      'mx-auto text-2xl font-bold md:text-center md:text-3xl',
                      {
                        'max-w-md': !!modalAsset,
                      }
                    )}
                  >
                    {modalTitle || (
                      <>
                        Sign up to use
                        <br />
                        advanced features
                      </>
                    )}
                  </div>
                  {modalSubtitle && (
                    <div className='mx-auto max-w-sm text-lg opacity-50 md:text-center md:text-lg'>
                      {modalSubtitle}
                    </div>
                  )}
                </div>

                {/* Auth Provider Buttons */}
                {!verificationId && !pendingEmailAuth && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className='md:w-full md:max-w-md'
                  >
                    <AuthProviderButtons
                      phoneNumber={phoneNumber}
                      validNumber={validNumber}
                      recaptchaVerifier={recaptchaVerifier}
                      setRecaptchaVerifier={setRecaptchaVerifier}
                      processing={processing}
                      setErrorMessage={setErrorMessage}
                      setProcessing={setProcessing}
                      setValidNumber={setValidNumber}
                      setPhoneNumber={setPhoneNumber}
                      setVerificationId={setVerificationId}
                    />
                  </motion.div>
                )}

                {/* Phone Number Verification */}
                {!!verificationId && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className='md:w-full md:max-w-md'
                  >
                    <PhoneNumberVerification
                      phoneNumber={phoneNumber}
                      verificationId={verificationId}
                      recaptchaVerifier={recaptchaVerifier}
                      setProcessing={setProcessing}
                      setErrorMessage={setErrorMessage}
                      onClick={() => {
                        setErrorMessage(null)
                        setVerificationId('')
                        setPhoneNumber('' as E164Number)
                      }}
                      callback={() => {
                        setType('onboarding')
                        setErrorMessage(null)
                        setVerificationId('')
                        setPhoneNumber('' as E164Number)
                      }}
                    />
                  </motion.div>
                )}

                {errorMessage && (
                  <div className='mx-auto my-4 text-sm font-semibold text-red-500 md:text-center'>
                    {errorMessage}
                  </div>
                )}
              </div>
            </div>

            {/* Terms and Close Button Container */}
            {!verificationId && (
              <div className='mx-auto mt-4 w-full max-w-4xl px-6 pb-8 md:px-12 md:pb-0'>
                {!disableClose && (
                  <div className='flex justify-center'>
                    <button
                      onClick={() => {
                        if (!authUserNotAnonymous) {
                          resetProcessing()
                        } else if (handleAuthSuccess) {
                          handleAuthSuccess(closeCallback)
                        }

                        setErrorMessage(null)

                        if (closeCallback) {
                          closeCallback()
                        }
                      }}
                      className='-mt-4 p-4 opacity-30 md:mt-0'
                    >
                      Close
                    </button>
                  </div>
                )}

                <div className='mx-auto max-w-[330px] text-center text-xs opacity-30 md:text-sm'>
                  By continuing, you agree to Dupe&apos;s{' '}
                  <a
                    target='_blank'
                    className='underline'
                    href='https://dupe.com/legal/terms?ref=dupe.com'
                  >
                    Terms of Service
                  </a>{' '}
                  and acknowledge you&apos;ve read our{' '}
                  <a
                    target='_blank'
                    className='underline'
                    href='https://dupe.com/legal/privacy?ref=dupe.com'
                  >
                    Privacy Policy
                  </a>
                </div>
              </div>
            )}
          </>
        )
      case 'phone':
        return (
          <div className='mx-auto flex max-w-md flex-col items-center justify-center space-y-2'>
            {!verificationId && (
              <div className='font-bold'>Verify your phone number</div>
            )}
            <div className='flex flex-col items-center justify-center space-y-5'>
              <div
                className={clsx(
                  'flex flex-row items-center justify-center space-x-2 transition-all duration-150 ease-linear',
                  {
                    hidden: !!verificationId,
                  }
                )}
              >
                <PhoneInputWrapper
                  phoneNumber={phoneNumber}
                  setPhoneNumber={setPhoneNumber}
                  onSubmit={() => handleSendCode('auth-link')}
                  processing={processing}
                  validNumber={validNumber}
                />
              </div>
              {authUser && !userAuthedWithPhone(authUser?.providerData) && (
                <button
                  onClick={() => setType('onboarding')}
                  className='font-cta text-sm uppercase text-gray-500 md:hover:underline'
                >
                  Skip
                </button>
              )}
            </div>

            <div
              className={clsx(
                'flex flex-col items-center justify-center space-y-2',
                {
                  hidden: !verificationId,
                }
              )}
            >
              <p className='mx-2 mt-2 text-center text-sm font-medium text-gray-700'>
                Code sent, it may take a few minutes to arrive.
                <br />
                <span
                  onClick={() => {
                    setVerificationId('')
                    setPhoneNumber('' as E164Number)
                  }}
                  className='mt-2 block cursor-pointer'
                >
                  (
                  <span className='font-semibold'>
                    {formatPhoneNumberIntl(phoneNumber)}
                  </span>
                  ) Wrong phone number?{' '}
                  <span className='cursor-pointer font-semibold'>Change</span>
                </span>
              </p>

              <div className='flex flex-row items-center justify-center '>
                <input
                  type='text'
                  inputMode='numeric'
                  pattern='[0-9]*'
                  maxLength={8}
                  value={verificationCode}
                  onChange={handleVerificationCodeChange}
                  className='dark:bg-darkinput flex w-full flex-row rounded-md rounded-r-none border-2 border-black bg-gray-300 px-4 py-2 pl-4 text-2xl dark:border-white/50'
                  placeholder='Enter code'
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleLinkPhone()
                    }
                  }}
                />
                <button
                  className='h-[52px] rounded rounded-l-none bg-blue-500 px-4 py-2 font-semibold text-white hover:bg-blue-700'
                  onClick={() => {
                    handleLinkPhone()
                  }}
                >
                  {processing ? (
                    <div className='animate-spin'>
                      <LoaderIcon height={18} />
                    </div>
                  ) : (
                    'Verify'
                  )}
                </button>
              </div>
            </div>

            {errorMessage && (
              <div className='mx-auto my-4 text-sm font-semibold text-red-500'>
                {errorMessage}
              </div>
            )}
          </div>
        )
      case 'onboarding':
        return renderOnboardingStep(closeCallback)
      default:
        return null
    }
  }

  // // If invite code is in url, open auth modal
  // useEffect(() => {
  //   if (!rewardsProgramFeatureEnabled) return
  //   if (isOpen) return
  //   if (authUser) return

  //   if (inviteCodeFromUrl) {
  //     openAuthModal({
  //       source: 'invite-code',
  //       modalTitle: `Join your friend on Dupe.com`,
  //       modalSubtitle: `Invite code ${inviteCodeFromUrl}`,
  //       fullScreen: false,
  //       blurOverlay: true,
  //       onClose: () => {
  //         clearInviteCode()
  //       },
  //     })
  //   }
  // }, [
  //   authUser,
  //   inviteCodeFromUrl,
  //   isOpen,
  //   openAuthModal,
  //   clearInviteCode,
  //   rewardsProgramFeatureEnabled,
  // ])

  // Set auth type based on user auth state
  useEffect(() => {
    if (loadingAuth) return
    if (loadingUserData) return

    // We use authUserNotAnonymous instead of authUser to also ensure that the user is not anonymous
    // As otherwise this would skip right to the onboarding step if the user is anonymous
    if (authUserNotAnonymous && !!authUser) {
      // check if user has phone number
      if (
        userAuthedWithPhone(authUser?.providerData) &&
        !userHasPhone(authUser?.providerData) &&
        !userPhoneLinked &&
        !skipPhoneLink
      ) {
        setType('phone')
        return
      }

      setType('onboarding')
    } else {
      setType('signin')
    }
  }, [
    authUser,
    authUserNotAnonymous,
    loadingAuth,
    recaptchaVerifier,
    skipPhoneLink,
    type,
    userPhoneLinked,
    loadingUserData,
  ])

  // Set user data in modal
  useEffect(() => {
    if (!userData) return

    if (userData.id) {
      // only update if current data is empty
      !firstName && userData?.firstName && setFirstName(userData.firstName)
      !email && userData?.email && setEmail(userData.email)
      !phoneNumber &&
        userData?.phone &&
        setPhoneNumber(userData.phone as E164Number)
    }
  }, [email, firstName, phoneNumber, userData])

  // Validate phone number
  useEffect(() => {
    if (!phoneNumber) return
    if (typeof phoneNumber !== 'string') return

    const valid = isValidPhoneNumber(phoneNumber)
    setValidNumber(valid)
  }, [phoneNumber])

  // Validate stripe session
  useValidateStripeSession({
    setProcessing,
    setErrorMessage,
  })

  return (
    <>
      {fullScreen && (
        <div className='absolute left-0 right-0 top-0 p-4'>
          <DupeLogo />
        </div>
      )}
      <div
        className={cn('flex flex-1 flex-col', {
          'pb-4 md:pb-16': !fullScreen,
          'pt-12 md:pt-16': fullScreen,
        })}
      >
        {renderContent(closeCallback)}
      </div>
    </>
  )
}

export default AuthContent
